/**
 * State restoration utilities for reconstructing streaming state from persisted data.
 * Used to render ProcessBar + LogicTree + DecisionSheet for historical runs.
 */

import type { FinancialStreamingState } from '@/components/verdict/FinancialLogicTree';
import type { StrategicStreamingState } from '@/hooks/useStrategicStream';

/**
 * Restore a FinancialStreamingState from persisted data.
 *
 * Accepts either:
 * - A `full_state` blob from `deep_dive_results` table (AnalysisDrawer path)
 * - A parsed verdict `blueprint` from `research_messages` (VerdictDashboard path)
 *
 * Both contain the same core fields; the function normalizes them into
 * a FinancialStreamingState with phase='concluded'.
 */
export function restoreFinancialState(data: Record<string, unknown>): FinancialStreamingState {
  return {
    phase: 'concluded',
    ticker: (data.ticker as string) || '',
    depth: (data.depth as 'fast' | 'deep') || 'fast',
    hardDataSnapshot: data.hardDataSnapshot as FinancialStreamingState['hardDataSnapshot'],
    bullAgents: (data.bullAgents as FinancialStreamingState['bullAgents']) || (data.bull_agents as FinancialStreamingState['bullAgents']) || [],
    bearAgents: (data.bearAgents as FinancialStreamingState['bearAgents']) || (data.bear_agents as FinancialStreamingState['bearAgents']) || [],
    debateMessages: (data.debateMessages as FinancialStreamingState['debateMessages']) || (data.debate_messages as FinancialStreamingState['debateMessages']) || [],
    debateRound: (data.debateRound as number) ?? (data.debate_round as number) ?? 0,
    agreementScore: (data.agreementScore as number) ?? (data.agreement_score as number) ?? 0,
    mediatorCompilation: (data.mediatorCompilation as FinancialStreamingState['mediatorCompilation']) || (data.mediator_compilation as FinancialStreamingState['mediatorCompilation']),
    killSheet: (data.killSheet as FinancialStreamingState['killSheet']) || (data.kill_sheet as FinancialStreamingState['killSheet']),
    convergenceTrajectory: (data.convergenceTrajectory as number[]) || (data.convergence_trajectory as number[]) || [],
    initialDivergence: (data.initialDivergence as number) ?? (data.initial_divergence as number) ?? 0,
    echoChamberedDetected: (data.echoChamberedDetected as boolean) ?? (data.echo_chamber_detected as boolean) ?? false,
    totalCreditsUsed: (data.totalCreditsUsed as number) ?? (data.total_credits_used as number),
    // Comparison mode fields
    isComparison: (data.isComparison as boolean) ?? (data.mode === 'comparison'),
    comparisonTickerA: data.comparisonTickerA as string,
    comparisonTickerB: data.comparisonTickerB as string,
    comparisonKillSheet: data.comparisonKillSheet as FinancialStreamingState['comparisonKillSheet'],
    comparisonSnapshotA: data.comparisonSnapshotA as FinancialStreamingState['comparisonSnapshotA'],
    comparisonSnapshotB: data.comparisonSnapshotB as FinancialStreamingState['comparisonSnapshotB'],
  };
}

/**
 * Restore a StrategicStreamingState from persisted `full_state` data.
 *
 * Used by both StrategicAnalysisPage (history tab) and VerdictDashboardPage
 * (session restore) to reconstruct the full streaming state for
 * StrategicLogicTree + StrategicDecisionSheet rendering.
 */
export function restoreStrategicState(data: Record<string, unknown>): StrategicStreamingState {
  return {
    phase: 'concluded',
    topic: (data.topic as string) || '',
    intensity: (data.intensity as StrategicStreamingState['intensity']) || 'fast',
    workstreamCount: (data.workstreamCount as number) ?? (data.workstream_count as number) ?? ((data.workstreams as unknown[])?.length || 0),
    totalAgents: (data.totalAgents as number) ?? (data.total_agents as number) ?? 0,
    estimatedSeconds: 0,
    workstreams: (data.workstreams as StrategicStreamingState['workstreams']) || [],
    conflicts: (data.conflicts as StrategicStreamingState['conflicts']) || [],
    stressTestResults: (data.stressTestResults as StrategicStreamingState['stressTestResults']) || [],
    debateArguments: (data.debateArguments as StrategicStreamingState['debateArguments']) || [],
    debateRound: (data.debateRounds as number) ?? (data.debate_rounds as number) ?? 0,
    debateRounds: (data.debateRounds as number) ?? (data.debate_rounds as number) ?? 0,
    mediatorConsensus: data.mediatorConsensus as StrategicStreamingState['mediatorConsensus'],
    ghostDeck: (data.ghostDeck as StrategicStreamingState['ghostDeck']) || [],
    deliverable: data.deliverable as StrategicStreamingState['deliverable'],
    competitorData: (data.competitorData as StrategicStreamingState['competitorData']) || [],
    startedAt: data.startedAt as string,
    completedAt: data.completedAt as string,
    totalCreditsUsed: (data.totalCreditsUsed as number) ?? (data.total_credits_used as number),
  };
}

/**
 * Check whether persisted data has enough information for full LogicTree reconstruction.
 * Returns false for legacy saves that only have kill_sheet/snapshot.
 */
export function hasFullFinancialState(data: Record<string, unknown>): boolean {
  return !!(data.bullAgents || data.bull_agents) && !!(data.bearAgents || data.bear_agents);
}
